import { useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { normalizeAttendanceTimeType } from './utils';

/**
 * 회사별 근무유형 선택지 조회 + 등록 모달에서의 즉시 추가.
 *   UserManagement.jsx 가 300줄에 닿아 분리했다(2026-09-08). 동작은 그대로다.
 */
export function useAttendanceTypeOptions({ apiBase, companyCode, showToast }) {
  // 초기값 빈 배열 — 회사별 근무유형은 fetch 로만 채운다(PINE 하드코딩 기본값 노출 방지).
  const [attendanceTypeOptions, setAttendanceTypeOptions] = useState([]);

  function getAttendanceTimeSettingsPath() {
    // 출퇴근 관리 useTimeSettings.js 'Fix B'와 동일 판별 — 'PINE' 하드코딩 금지.
    //   ext 화면(apiBase=/api/admin/ext/{CODE}/users, 운영자/외부권한자)만 ext 라우트.
    //   자기 회사 화면(/admin/users: 운영자=PINE, 테넌트=자기 회사)은 메인 라우트 —
    //   메인 라우트가 고객 테넌트를 자기 회사로 스코프하며, 테넌트 관리자 파생 권한은
    //   일반 코드(USER_MANAGEMENT 등)뿐이라 ext 라우트(EXTERNAL_{CODE}_* 요구)에선 403이 났다.
    if (String(apiBase || '').startsWith('/api/admin/ext/')) {
      return `/api/admin/ext/${encodeURIComponent(companyCode)}/attendance-time-settings`;
    }
    return '/api/admin/attendance-time-settings';
  }

  async function fetchAttendanceTypeOptions() {
    const result = await apiClient.get(getAttendanceTimeSettingsPath());
    if (!result.ok) return;

    const templates = Array.isArray(result.data?.data?.templates) ? result.data.data.templates : [];
    const names = templates
      .map((row) => String(row?.name || '').trim())
      .filter((name, index, arr) => Boolean(name) && arr.indexOf(name) === index);

    // API 성공 시 그 회사가 실제 설정한 근무유형만 사용(없으면 빈 목록 → '없음'만). 하드코딩 폴백(풀타임/파트타임)은
    //   API 실패(!result.ok, 위에서 return) 때만 유지 — 설정 안 한 신규 테넌트에 PINE식 기본값이 뜨지 않게.
    setAttendanceTypeOptions(names);
  }

  // 직원 등록 모달에서 근무유형 바로 추가 (2026-08-28 사용자 명세) —
  //   오른쪽 설정 패널(WorkTypeQuickAddPanel)에서 만든 요일 규칙 전체를 받아 정식 템플릿으로 저장.
  //   성공 시 선택지 새로고침 후 {ok, name} 반환.
  async function createAttendanceType(input) {
    const name = String(input?.name || '').trim();
    if (!name) return { ok: false, message: '근무유형 이름을 입력하세요.' };
    if (name.length > 20) return { ok: false, message: '이름은 20자 이하로 해주세요.' };
    if (attendanceTypeOptions.includes(name)) return { ok: false, message: '이미 있는 근무유형입니다.' };
    const path = getAttendanceTimeSettingsPath();
    const cur = await apiClient.get(path);
    if (!cur.ok) return { ok: false, message: '시간 설정을 불러오지 못했습니다(권한 확인).' };
    const data = cur.data?.data || {};
    const template = {
      id: `t${Date.now()}`,
      name,
      weekly_rules: input?.weekly_rules || {},
      prescribed_daily_hours: input?.prescribed_daily_hours ?? null,
    };
    const put = await apiClient.put(path, {
      // 메인 라우트용 회사 지정(운영자=명시값, 고객 테넌트=서버가 자기 회사로 강제). ext 라우트는 경로 파라미터라 무시됨.
      company_code: companyCode,
      policy: data.policy || { late_grace_minutes: 0, early_leave_grace_minutes: 0 },
      templates: [...(Array.isArray(data.templates) ? data.templates : []), template],
    });
    if (!put.ok) return { ok: false, message: put.data?.message || '근무유형 추가에 실패했습니다(출퇴근 관리 권한 필요).' };
    await fetchAttendanceTypeOptions();
    showToast(`근무유형 '${name}'을(를) 추가했습니다.`, 'success');
    return { ok: true, name };
  }

  useEffect(() => {
    setAttendanceTypeOptions([]);
    fetchAttendanceTypeOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCode]);

  return {
    attendanceTypeOptions,
    createAttendanceType,
    getDefaultAttendanceType: () => normalizeAttendanceTimeType('', attendanceTypeOptions),
  };
}
